import React, { useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { Image as ImageIcon, UploadCloud, Link2, FileDown } from "lucide-react";

export default function Photos() {
  const [mode, setMode] = useState("single");
  const [jewelCode, setJewelCode] = useState("");
  const [singleFile, setSingleFile] = useState(null);
  const [photoUrl, setPhotoUrl] = useState("");
  const [bulkFile, setBulkFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const singleRef = useRef();
  const bulkRef = useRef();

  const uploadSingle = async () => {
    if (!jewelCode) return toast.error("Jewel Code required");
    if (!singleFile) return toast.error("Choose a photo");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("jewel_code", jewelCode);
      fd.append("file", singleFile);
      const { data } = await api.post("/photos/single", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo linked");
      setResult({ ok: true, message: `Linked photo to ${jewelCode}` });
      setSingleFile(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  const linkUrl = async () => {
    if (!jewelCode || !photoUrl) return toast.error("Jewel Code and URL required");
    setBusy(true);
    try {
      await api.post("/photos/url", { jewel_code: jewelCode, photo_url: photoUrl });
      toast.success("URL linked");
      setPhotoUrl("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const uploadBulk = async () => {
    if (!bulkFile) return toast.error("Choose an Excel file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", bulkFile);
      const { data } = await api.post("/photos/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.ok) toast.success(`${data.linked} photos linked`);
      else toast.error(data.error || "Failed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/photos/template`;
    const tok = localStorage.getItem("access_token");
    fetch(url, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} })
      .then((r) => r.blob()).then((b) => {
        const u = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = u; a.download = "photo_template.xlsx"; a.click();
        URL.revokeObjectURL(u);
      });
  };

  return (
    <div className="space-y-8" data-testid="photos-page">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-2">Photo Module</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B2A]">Photos</h1>
        <p className="text-slate-500 mt-2">Attach product photos by upload or external URL — single or in bulk.</p>
      </div>

      <div className="inline-flex p-1 bg-white border border-[#0D1B2A]/10 rounded-md">
        <button onClick={() => setMode("single")} data-testid="mode-single"
          className={`px-5 py-2 text-sm rounded-md ${mode === "single" ? "bg-[#0D1B2A] text-white" : "text-slate-600"}`}>Single</button>
        <button onClick={() => setMode("bulk")} data-testid="mode-bulk"
          className={`px-5 py-2 text-sm rounded-md ${mode === "bulk" ? "bg-[#0D1B2A] text-white" : "text-slate-600"}`}>Bulk</button>
      </div>

      {mode === "single" ? (
        <div className="luxe-card p-8 space-y-6 max-w-2xl">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Jewel Code</label>
            <Input value={jewelCode} onChange={(e) => setJewelCode(e.target.value)} placeholder="e.g. 24P0268" className="h-11" data-testid="single-jewel" />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Upload file</label>
              <div onClick={() => singleRef.current?.click()}
                className="border-2 border-dashed border-[#0D1B2A]/15 rounded-md p-6 text-center cursor-pointer hover:border-[#C9A84C]">
                <UploadCloud className="w-7 h-7 mx-auto text-[#C9A84C] mb-2" strokeWidth={1.5} />
                <p className="text-sm text-slate-600">{singleFile ? singleFile.name : "Choose image"}</p>
                <input ref={singleRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => setSingleFile(e.target.files[0])} data-testid="single-file" />
              </div>
              <Button onClick={uploadSingle} disabled={busy} className="mt-3 w-full bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid="single-upload-btn">
                Upload photo
              </Button>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Or paste URL</label>
              <Input value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} placeholder="https://…/photo.jpg" className="h-11" data-testid="single-url" />
              <Button onClick={linkUrl} disabled={busy} className="mt-3 w-full bg-[#C9A84C] hover:bg-[#C9A84C]/90 text-white" data-testid="link-url-btn">
                <Link2 className="w-4 h-4 mr-2" /> Link URL
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="luxe-card p-8 space-y-6 max-w-2xl">
          <Button variant="outline" onClick={downloadTemplate} className="border-[#0D1B2A]/20" data-testid="dl-template">
            <FileDown className="w-4 h-4 mr-2" /> Download template
          </Button>
          <div onClick={() => bulkRef.current?.click()}
            className="border-2 border-dashed border-[#0D1B2A]/15 rounded-md p-8 text-center cursor-pointer hover:border-[#C9A84C]">
            <UploadCloud className="w-8 h-8 mx-auto text-[#C9A84C] mb-2" strokeWidth={1.5} />
            <p className="text-sm text-slate-600">{bulkFile ? bulkFile.name : "Upload filled template (.xlsx)"}</p>
            <input ref={bulkRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => setBulkFile(e.target.files[0])} data-testid="bulk-file" />
          </div>
          <Button onClick={uploadBulk} disabled={busy || !bulkFile} className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid="bulk-upload-btn">
            Process bulk upload
          </Button>
        </div>
      )}

      {result && (
        <div className="luxe-card p-6">
          <div className="text-sm text-[#0D1B2A]">
            {result.ok === true || result.message ? (
              <>
                <ImageIcon className="w-5 h-5 inline-block text-[#C9A84C] mr-2" />
                {result.message || `${result.linked} photos linked.`}
                {result.not_found?.length > 0 && (
                  <div className="text-xs text-red-600 mt-2">
                    Jewel Codes not found: {result.not_found.slice(0, 20).join(", ")}{result.not_found.length > 20 ? "…" : ""}
                  </div>
                )}
              </>
            ) : (
              <div className="text-red-600">{result.error}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
